// src/components/layout/UnderHeader.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchProducts } from "../../redux/features/Product/productThunk";

// ✅ تابع اصلاح‌شده برای استخراج نام دسته‌بندی
const getCategoryName = (product) => {
  if (!product) return "بدون دسته";
  
  if (product.category_name) {
    return product.category_name;
  }
  
  if (product.category && typeof product.category === "object") {
    return product.category.name || product.category.title || "بدون دسته";
  }
  
  if (product.category && typeof product.category === "string") {
    if (!isNaN(product.category)) {
      return product.type || "بدون دسته";
    }
    return product.category;
  }
  
  if (product.type) return product.type;
  
  return "بدون دسته";
};

function UnderHeader() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { products, loading } = useSelector((state) => state.listProduct) || { products: [] };

  const [isOpen, setIsOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const [hoveredProduct, setHoveredProduct] = useState(null);

  useEffect(() => {
    if (!products || products.length === 0) {
      dispatch(fetchProducts());
    }
  }, [dispatch, products]);

  const menuData = useMemo(() => {
    if (!products || products.length === 0) return [];

    const categories = [...new Set(products.map((p) => getCategoryName(p)))].filter(
      (cat) => cat && cat !== "بدون دسته"
    );

    return categories.map((cat) => ({
      name: cat,
      products: products.filter((p) => getCategoryName(p) === cat),
      subs: [
        ...new Set(
          products
            .filter((p) => getCategoryName(p) === cat && p.type && p.type !== cat)
            .map((p) => p.type)
        ),
      ].filter(Boolean),
    }));
  }, [products]);

  const getProductsByType = (categoryName, type) => {
    return products.filter((p) => getCategoryName(p) === categoryName && p.type === type);
  };

  if (loading) {
    return (
      <div className="hidden lg:flex justify-between items-center w-full h-[54px] bg-white/70 backdrop-blur-md border-b border-gray-100 px-6 shadow-sm">
        <div className="flex items-center gap-6">
          <div className="w-44 h-9 bg-gray-200/80 animate-pulse rounded-xl"></div>
          <div className="w-72 h-4 bg-gray-200/80 animate-pulse rounded-md"></div>
        </div>
        <div className="w-48 h-9 bg-gray-200/80 animate-pulse rounded-xl"></div>
      </div>
    );
  }

  return (
    <nav 
      aria-label="منوی ثانویه" 
      dir="rtl"
      className="hidden lg:flex justify-between items-center w-full h-[54px] bg-white/5 backdrop-blur-md border-b border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.03)] px-4 xl:px-8 select-none relative z-[888]"
    >
      <div className="flex items-center gap-6 xl:gap-8 h-full">
        
        <div
          className="relative flex items-center h-full"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => {
            setIsOpen(false);
            setActiveCat(null);
            setHoveredProduct(null);
          }}
        >
          <button 
            type="button"
            aria-expanded={isOpen}
            onClick={() => setIsOpen(!isOpen)} // ✅ افزودن قابلیت کلیک برای دستگاه‌های لمسی
            className="flex items-center justify-between gap-3 bg-[#8A9A7B] hover:bg-[#7d8c6e] text-white w-[210px] h-[38px] px-3.5 rounded-xl shadow-sm transition-all duration-200 ease-out active:scale-[0.98]"
          >
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="16" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>

            <span className="text-[13px] font-semibold tracking-tight">دسته‌بندی محصولات</span>

            <svg 
              className={`w-3.5 h-3.5 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} 
              fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {isOpen && (
            <div className="absolute top-[48px] right-0 z-[10000] flex animate-in fade-in slide-in-from-top-2 duration-200">
              
              <div className="w-[230px] overflow-hidden rounded-2xl border border-gray-100 bg-white/95 backdrop-blur-xl py-2 shadow-xl">
                {menuData.length === 0 ? (
                  <div className="px-4 py-3 text-[13px] text-gray-400 text-center">
                    هیچ دسته‌بندی موجود نیست
                  </div>
                ) : (
                  menuData.map((item, idx) => {
                    const isActive = activeCat?.name === item.name;
                    return (
                      <div
                        key={idx}
                        onMouseEnter={() => {
                          setActiveCat(item);
                          setHoveredProduct(null);
                        }}
                        className={`flex cursor-pointer items-center justify-between px-4 py-2.5 text-[13px] transition-colors duration-150 ${
                          isActive
                            ? "bg-[#8A9A7B]/10 text-[#8A9A7B] font-bold border-r-4 border-[#8A9A7B]"
                            : "text-gray-700 hover:bg-gray-50/80 font-medium"
                        }`}
                      >
                        <span className="truncate">{item.name}</span>
                        {item.subs.length > 0 && (
                          <svg className="h-3.5 w-3.5 text-gray-400 rotate-180 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {activeCat && (
                <div className="mr-2 w-[340px] rounded-2xl border border-gray-100 bg-white/95 backdrop-blur-xl p-4 shadow-xl">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-2.5 mb-3">
                    <span className="text-[13px] font-bold text-gray-800">{activeCat.name}</span>
                    <span className="text-[11px] font-medium text-gray-400">{activeCat.products.length} محصول</span>
                  </div>

                  {activeCat.subs.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {activeCat.subs.map((sub, idx) => {
                        const subProducts = getProductsByType(activeCat.name, sub);
                        const isSubHovered = hoveredProduct?.type === sub;
                        return (
                          <button
                            key={idx}
                            type="button"
                            onMouseEnter={() => setHoveredProduct({ type: sub, products: subProducts })}
                            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${
                              isSubHovered
                                ? "bg-[#8A9A7B] text-white shadow-sm"
                                : "bg-gray-100/70 text-gray-600 hover:bg-[#8A9A7B]/15 hover:text-[#8A9A7B]"
                            }`}
                          >
                            {sub}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="max-h-[260px] overflow-y-auto space-y-2 pr-1">
                    {(hoveredProduct ? hoveredProduct.products : activeCat.products).slice(0, 5).map((product) => (
                      <div
                        key={product._id || product.id}
                        onClick={() => {
                          navigate(`/product/${product._id || product.id}`);
                          setIsOpen(false);
                        }}
                        className="flex cursor-pointer items-center gap-3 rounded-xl p-1.5 transition hover:bg-gray-50/90 border border-transparent hover:border-gray-100"
                      >
                        <img
                          src={product.image || product.images?.[0] || "https://via.placeholder.com/48"}
                          alt={product.name || product.title}
                          className="h-10 w-10 rounded-lg object-cover bg-gray-50 border border-gray-100"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-medium text-gray-800 truncate">{product.name || product.title}</p>
                          <p className="text-[11px] font-bold text-[#8A9A7B] mt-0.5">
                            {Number(product.price || 0).toLocaleString("fa-IR")} تومان
                          </p>
                        </div>
                      </div>
                    ))}

                    {activeCat.products.length > 5 && (
                      <button
                        type="button"
                        onClick={() => {
                          navigate(`/products?category=${encodeURIComponent(activeCat.name)}`);
                          setIsOpen(false);
                        }}
                        className="w-full text-center text-[11px] font-bold text-[#8A9A7B] hover:text-[#6f7e61] transition pt-2"
                      >
                        مشاهده همه محصولات این دسته ←
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <ul className="flex items-center gap-6 xl:gap-7 text-[13px] font-medium text-gray-600 m-0 p-0 list-none">
          <li
            onClick={() => navigate("/")}
            className="cursor-pointer hover:text-[#8A9A7B] transition-colors inline-flex items-center h-full"
          >
            فروشگاه
          </li>
          <li
            onClick={() => navigate("/about")}
            className="cursor-pointer hover:text-[#8A9A7B] transition-colors inline-flex items-center h-full"
          >
            درباره ما
          </li>
          <li
            onClick={() => navigate("/faq")}
            className="cursor-pointer hover:text-[#8A9A7B] transition-colors inline-flex items-center h-full"
          >
            سوالات متداول
          </li>
          {/* ✅ لینک وبلاگ حذف شد */}
          <li
            onClick={() => navigate("/discount")}
            className="cursor-pointer hover:text-[#8A9A7B] transition-colors inline-flex items-center h-full"
          >
            تخفیفات
          </li>
        </ul>
      </div>

      <div className="flex items-center gap-4 h-full">
        <button 
          type="button"
          onClick={() => navigate("/contact")}
          className="h-[36px] px-4 bg-[#9EAD8C] hover:bg-[#8e9c7d] text-white rounded-xl text-[12px] font-medium transition-all shadow-sm active:scale-[0.98] flex items-center justify-center"
        >
          سوالی دارید؟ با ما صحبت کنید
        </button>

        <a
          href="tel:09903607525"
          className="flex items-center gap-2 pl-1 hover:opacity-80 transition"
        >
          <span className="text-[13px] font-semibold text-gray-700 tracking-wider font-mono">
            09903607525
          </span>
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 shadow-inner">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91 a16 16 0 0 0 6 6l.99-1.27 a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </div>
        </a>
      </div>
    </nav>
  );
}

export default UnderHeader;